import axios from "axios";
import router from "@/router";

import { API_BASE_URL } from "./baseUrlConfig";

export { API_BASE_URL };

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

const AUTH_MSG_REGEX =
  /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/i;

const RETRY_STATUS_CODES = [
  408,
  429,
  500,
  502,
  503,
  504,
];

const DASHBOARD_SAFE_POST_ENDPOINTS = [
  "/web/back-office-dashboard/invoicedetails",
  "/web/back-office-dashboard/targetachievement-new",
  "/web/back-office-dashboard/distributorstocks",
  "/web/back-office-dashboard/get-lorry-wise-sales",
  "/web/back-office-dashboard/get-lorry-wise-sales-details",
  "/web/back-office-dashboard/get-sku-wise-sales-stats",
  "/web/back-office-dashboard/get-sku-wise-sales-details",
  "/productive-stats-new",
  "/web/map-payments",
  "/web/dealer-map",
];

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isAuthError(status, message) {
  const messageMatches = AUTH_MSG_REGEX.test(
    message ?? "",
  );

  if (status === 401) {
    return messageMatches;
  }

  return (
    status === 419 ||
    status === 440 ||
    status === 498 ||
    messageMatches
  );
}

function handleSessionExpiry() {
  const mobileNumber =
    localStorage.getItem("mobileNumber");

  localStorage.clear();
  sessionStorage.clear();

  if (mobileNumber !== null) {
    localStorage.setItem(
      "mobileNumber",
      mobileNumber,
    );
  }

  router.replace("/login");
}

function isDashboardSafeRequest(config) {
  const method = String(
    config?.method || "",
  ).toLowerCase();

  const url = String(config?.url || "");

  if (method === "get") {
    return (
      url.includes(
        "/web/back-office-dashboard/",
      ) ||
      url.includes("/web/dashboard-") ||
      url.includes(
        "/web/dashboard-dealer-map/",
      )
    );
  }

  if (method === "post") {
    return DASHBOARD_SAFE_POST_ENDPOINTS.some(
      (endpoint) => url.includes(endpoint),
    );
  }

  return false;
}

function shouldRetry(error) {
  const status = error?.response?.status;

  return (
    !error?.response ||
    error?.code === "ECONNABORTED" ||
    RETRY_STATUS_CODES.includes(status)
  );
}

/**
 * Axios can serialize config.data into a JSON string.
 * This commonly happens before a retry request is made.
 *
 * Convert JSON strings back into objects before adding
 * web_user_id and web_page.
 */
function normalizeJsonRequestData(data) {
  if (data === undefined || data === null) {
    return {};
  }

  if (typeof data === "string") {
    try {
      const parsedData = JSON.parse(data);

      if (
        parsedData &&
        typeof parsedData === "object" &&
        !Array.isArray(parsedData)
      ) {
        return parsedData;
      }

      return {};
    } catch (error) {
      console.warn(
        "Unable to parse request data as JSON:",
        error,
      );

      return {};
    }
  }

  if (
    typeof data === "object" &&
    !Array.isArray(data)
  ) {
    return data;
  }

  return {};
}

/**
 * Prevent duplicate FormData tracking fields when Axios
 * retries the same request.
 */
function setFormDataValue(
  formData,
  fieldName,
  value,
) {
  if (typeof formData.set === "function") {
    formData.set(fieldName, value ?? "");
    return;
  }

  formData.delete?.(fieldName);
  formData.append(fieldName, value ?? "");
}

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    /*
     * Keep the same request ID during retries.
     * This makes backend tracing much clearer.
     */
    if (!config.headers["X-Request-Id"]) {
      config.headers["X-Request-Id"] =
        `${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`;
    }

    const method = String(
      config.method || "",
    ).toLowerCase();

    if (method === "post") {
      const webUserId =
        localStorage.getItem("user_id") || null;

      const webPage =
        window.location.pathname;

      if (config.data instanceof FormData) {
        setFormDataValue(
          config.data,
          "web_user_id",
          webUserId,
        );

        setFormDataValue(
          config.data,
          "web_page",
          webPage,
        );
      } else {
        const normalizedData =
          normalizeJsonRequestData(config.data);

        config.data = {
          ...normalizedData,
          web_user_id: webUserId,
          web_page: webPage,
        };
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    const message =
      response?.data?.message ??
      response?.data?.error ??
      "";

    if (AUTH_MSG_REGEX.test(message)) {
      handleSessionExpiry();

      return Promise.reject(response);
    }

    return response;
  },

  async (error) => {
    const config = error?.config || {};
    const status = error?.response?.status;

    const message =
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      "";

    if (isAuthError(status, message)) {
      handleSessionExpiry();

      return Promise.reject(error);
    }

    const canRetry =
      !config.skipDashboardInterceptorRetry &&
      isDashboardSafeRequest(config) &&
      shouldRetry(error);

    config.__retryCount =
      config.__retryCount || 0;

    if (
      canRetry &&
      config.__retryCount < 2
    ) {
      config.__retryCount += 1;

      const retryAfter = Number(
        error?.response?.headers?.[
          "retry-after"
        ],
      );

      const delay = retryAfter
        ? retryAfter * 1000
        : 700 * config.__retryCount +
          Math.floor(Math.random() * 300);

      /*
       * Axios may have serialized the original object
       * before this retry. Normalize it now as well.
       */
      if (
        typeof config.data === "string"
      ) {
        try {
          config.data = JSON.parse(
            config.data,
          );
        } catch (parseError) {
          console.warn(
            "Unable to parse retry request body:",
            parseError,
          );
        }
      }

      await sleep(delay);

      return axiosInstance(config);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;